import { useFormikContext } from 'formik';

import { FormControl } from '@@/form-components/FormControl';
import { FormSection } from '@@/form-components/FormSection';
import { Input } from '@@/form-components/Input';
import { InputGroup } from '@@/form-components/InputGroup';
import { InputList } from '@@/form-components/InputList';
import { PortainerSelect } from '@@/form-components/PortainerSelect';
import { SegmentedControl } from '@@/form-components/SegmentedControl';
import { TextTip } from '@@/Tip/TextTip';
import { ExternalLink } from '@@/ExternalLink';

import { firstNamespaceError } from '../../../utils/namespace';

import { NamespacePatternItem } from './NamespacePatternItem';
import { SeveritySelector } from './SeveritySelector';
import {
  activeNamespaceField,
  MAX_CONCURRENT_SCANS,
  MIN_CONCURRENT_SCANS,
  NamespaceScopeMode,
  RESCAN_INTERVAL_UNIT_OPTIONS,
  RescanIntervalUnit,
  VulnerabilityScanningFormValues,
} from './types';

const TRIVY_OPERATOR_DOCS_URL =
  'https://aquasecurity.github.io/trivy-operator/latest/';

const SCOPE_MODE_ITEMS: Array<{ id: NamespaceScopeMode; label: string }> = [
  { id: 'all-except', label: 'All namespaces except' },
  { id: 'only', label: 'Only these namespaces' },
];

export function VulnerabilityScanningFormFields() {
  const { values, setFieldValue, errors } =
    useFormikContext<VulnerabilityScanningFormValues>();

  const mode = values.namespaceScopeMode;
  const field = activeNamespaceField(mode);
  const fieldError = errors[field];
  const namespacesError = firstNamespaceError(fieldError);

  return (
    <>
      <FormSection title="About vulnerability scanning">
        <p className="text-muted text-sm">
          Attaching this policy deploys{' '}
          <ExternalLink to={TRIVY_OPERATOR_DOCS_URL} data-cy="trivy-docs-link">
            trivy-operator
          </ExternalLink>{' '}
          to every environment in the selected groups, including environments
          added later. It scans the images of running workloads on a schedule
          and records the findings in the cluster. Any changes to the nominated
          namespaces will also trigger a new scan.
        </p>
        <p className="text-muted mt-2 text-sm">
          Scanning is real work on a busy cluster. Keep the interval long and
          the concurrency low unless you have headroom to spare.
        </p>
      </FormSection>

      <FormSection title="Scanning">
        <FormControl
          label="Rescan running workloads every"
          inputId="rescan-interval"
          errors={errors.rescanIntervalValue}
        >
          <InputGroup className="flex w-48">
            <Input
              id="rescan-interval"
              type="number"
              min={1}
              step={1}
              value={values.rescanIntervalValue}
              onChange={(e) =>
                setFieldValue('rescanIntervalValue', Number(e.target.value))
              }
              data-cy="vulnerability-scanning-rescan-interval"
              className="!w-1/2 !border-r-0"
            />
            <div className="w-1/2">
              <PortainerSelect<RescanIntervalUnit>
                value={values.rescanIntervalUnit}
                options={[...RESCAN_INTERVAL_UNIT_OPTIONS]}
                onChange={(unit) =>
                  setFieldValue('rescanIntervalUnit', unit ?? 'days')
                }
                data-cy="vulnerability-scanning-rescan-unit"
              />
            </div>
          </InputGroup>
        </FormControl>

        <FormControl
          label="Maximum concurrent scans"
          inputId="max-concurrent-scans"
          errors={errors.maxConcurrentScans}
          tooltip="How many scan jobs may run at once. A higher limit finishes a sweep sooner but takes more of the cluster while it runs."
        >
          <Input
            id="max-concurrent-scans"
            type="number"
            min={MIN_CONCURRENT_SCANS}
            max={MAX_CONCURRENT_SCANS}
            step={1}
            value={values.maxConcurrentScans}
            onChange={(e) =>
              setFieldValue('maxConcurrentScans', Number(e.target.value))
            }
            data-cy="vulnerability-scanning-max-concurrent-scans"
            className="w-24"
          />
        </FormControl>

        <FormControl
          label="Record findings of severity"
          inputId="severity-critical"
          errors={errors.severities}
          tooltip="Which severities the scanner records. Unrecorded severities are not stored at all, so they cannot be shown or alerted on without a rescan. Low and Unknown are usually the bulk of findings and rarely actionable."
        >
          <SeveritySelector
            value={values.severities}
            onChange={(severities) => setFieldValue('severities', severities)}
          />
        </FormControl>
      </FormSection>

      <FormSection title="Namespace scope">
        <SegmentedControl
          label="Namespace scope"
          items={SCOPE_MODE_ITEMS}
          activeId={mode}
          onChange={(id) => setFieldValue('namespaceScopeMode', id)}
          className="mb-3"
        />

        <TextTip color="blue">
          {mode === 'all-except'
            ? 'Every namespace is scanned except those listed. Entries may use glob patterns, such as kube-*.'
            : 'Only the namespaces listed are scanned. Namespaces created later are not covered until you add them here.'}
        </TextTip>

        <InputList<string>
          key={field}
          value={values[field]}
          onChange={(namespaces) => setFieldValue(field, namespaces)}
          itemBuilder={() => ''}
          item={NamespacePatternItem}
          addLabel="Add namespace"
          errors={Array.isArray(fieldError) ? fieldError : undefined}
          data-cy="vulnerability-scanning-namespaces"
        />

        {namespacesError && <TextTip color="orange">{namespacesError}</TextTip>}
      </FormSection>

      <FormSection title="Advanced">
        <FormControl
          label="Operator namespace"
          inputId="target-namespace"
          errors={errors.targetNamespace}
          tooltip="The namespace trivy-operator itself runs in. This is not what it scans."
        >
          <Input
            id="target-namespace"
            value={values.targetNamespace}
            onChange={(e) => setFieldValue('targetNamespace', e.target.value)}
            data-cy="vulnerability-scanning-target-namespace"
            className="w-72"
          />
        </FormControl>
      </FormSection>
    </>
  );
}
